<template>
  <card-container
    :id="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerMessage"
    :disabled="isSubmitBtnDisabled"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      {{ formTitle }}
    </template>

    <template #content>
      <h6 class="sub-section-title">
        Provider Information
      </h6>
      <ProviderPersonalInfo
        v-model="provider"
        @update-email-validation="updateEmailValidation"
      />

      <PaymentOptions v-model="provider" />

      <h6 class="sub-section-title">
        Service Location
      </h6>
      <ProviderServiceLocation v-model="provider" />

      <h6 class="sub-section-title flex-space-between align-items-end">
        <span>Service Types & Rates</span>
        <p-button
          type="purple"
          block
          :icon="{ iconName: 'fa-plus' }"
          @click="onAddProviderRate"
        >
          Add Service
        </p-button>
      </h6>
      <ProviderRate
        v-for="(item, index) in providerRates"
        :key="index"
        v-model="providerRates[index]"
        :index="index"
        :removeable="providerRates.length > 1"
        @delete="onDeleteRate(index)"
      >
        <template #title>
          {{ `Service ${index + 1}` }}
        </template>
      </ProviderRate>
    </template>

    <template #submit-button>
      {{ submitBtnText }}
    </template>

    <template #additional-buttons>
      <p-button
        type="warning"
        round
        @click="backToList()"
      >
        Back to List
      </p-button>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, computed, onMounted, defineAsyncComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { omit, cloneDeep } from 'lodash';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { homeServiceOrderService } from '@/services/homeServiceOrder';
import { catchErrorMessageBox } from '@/Utils/catchErrorMessageBox';
import { HomeServiceProviderInputType } from '@/gql/graphql';
import { HomeServiceProvider, HomeServiceProviderSearchType, HomeServiceProviderRate, CurrencyType } from '@/models/homeService';
import { providerSourceList } from '@/constants/homeService';

const ProviderPersonalInfo = defineAsyncComponent(() => import('./Provider/ProviderPersonalInfo.vue') as any);
const ProviderServiceLocation = defineAsyncComponent(() => import('./Provider/ProviderServiceLocation.vue') as any);
const ProviderRate = defineAsyncComponent(() => import('./Provider/ProviderRate.vue') as any);
const PaymentOptions = defineAsyncComponent(() => import('./Provider/PaymentOptions.vue') as any);

const pageId = 'create-update-home-service-provider';

const route = useRoute();
const router = useRouter();
const { showSuccessPopupDialog } = useSwalDialog();

const providerId = ref(0);
const provider: Ref<HomeServiceProvider> = ref({
  id: 0,
  firstName: '',
  lastName: '',
  phoneNumber: '',
  email: null,
  city: '',
  state: '',
  zip: null,
  businessName: null,
  source: null,
  otherSource: '',
  providerNotes: '',
  isActive: true,
  backgroundCheckedAt: null,
  backgroundCheckSourced: null,
  paymentOptions: [],
  professionalProfileUrl: '',
});
const originalProvider: Ref<HomeServiceProvider | null> = ref(null);

const providerRates: Ref<HomeServiceProviderRate[]> = ref([
  {
    id: 0,
    providerId: 0,
    serviceType: null,
    currency: CurrencyType.Usd,
    hourlyRate: null,
  },
]);
const status = reactive({
  isLoading: false,
  spinnerMessage: '',
  isEmailValid: true,
});

const formTitle = computed(() => (providerId.value ? 'Update Provider' : 'Create Provider'));
const submitBtnText = computed(() => (providerId.value ? 'Update Provider' : 'Create Provider'));
const isProfessionalProfileUrlValid = computed(() => {
  if (!provider.value.professionalProfileUrl) {
    return true;
  }

  return util.isValidUrl(provider.value.professionalProfileUrl);
});
const isSubmitBtnDisabled = computed(() => status.isLoading || !status.isEmailValid || !isProfessionalProfileUrlValid.value);

const updateEmailValidation = (value: boolean) => {
  status.isEmailValid = value;
};

const onAddProviderRate = () => {
  providerRates.value.push({
    id: 0,
    providerId: 0,
    serviceType: null,
    currency: CurrencyType.Usd,
    hourlyRate: 0,
  });
};

const onDeleteRate = (index: number) => {
  providerRates.value.splice(index, 1);
};

const loadProvider = async () => {
  const paramId = route.params.id as string;

  if (!paramId) {
    return;
  }

  status.spinnerMessage = 'Loading Provider Information...';
  status.isLoading = true;
  catchErrorMessageBox(async () => {
    const response = await homeServiceOrderService.fetchHomeServiceProviders({
      searchType: HomeServiceProviderSearchType.Id,
      searchValue: paramId,
    });

    if (response.count === 0) {
      return;
    }

    providerId.value = Number(paramId);
    provider.value = response.rows[0];
    adjustSource();
    providerRates.value = provider.value.rates ?? [];
    originalProvider.value = cloneDeep(provider.value);
  })
    .finally(() => (status.isLoading = false))
    .catch(() => (providerId.value = 0));
};

const adjustSource = () => {
  if (!provider.value.source) {
    return;
  }

  const sourceIndex = providerSourceList.findIndex(item => item.key === provider.value.source);

  if (sourceIndex !== -1) {
    return;
  }

  provider.value.otherSource = provider.value.source ?? '';
  provider.value.source = 'Other';
}

const backToList = () => {
  router.push({
    name: 'HomeServiceProviders',
  });
};

const submitForm = async () => {
  const data: HomeServiceProviderInputType = omit(provider.value, ['id', 'createdAt', 'updatedAt', 'otherSource']);
  data.rates = providerRates.value.map((item: HomeServiceProviderRate) => ({
    id: item.id,
    serviceType: item.serviceType,
    hourlyRate: item.hourlyRate ? Number(item.hourlyRate) : null,
    currency: item.currency,
  }));
  data.phoneNumber = util.getValidPhoneNumber(data.phoneNumber);
  if (data.source === 'Other') {
    data.source = provider.value.otherSource || data.source;
  }

  status.spinnerMessage = providerId.value ? 'Updating Provider...' : 'Creating Provider...';
  status.isLoading = true;
  catchErrorMessageBox(async () => {
    providerId.value
      ? await homeServiceOrderService.updateHomeServiceProvider({
          id: providerId.value,
          data,
        })
      : await homeServiceOrderService.createHomeServiceProvider(data);

    await showSuccessPopupDialog({
      text: providerId.value ? 'Provider has been updated successfully!' : 'Provider has been created successfully!',
    });

    backToList();
  })
    .finally(() => (status.isLoading = false))
    .catch(() => null);
};

const resetForm = () => {
  provider.value = providerId.value && originalProvider.value ? cloneDeep(originalProvider.value) : {
    id: 0,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: null,
    city: '',
    state: '',
    zip: null,
    businessName: null,
    source: null,
    otherSource: '',
    providerNotes: '',
    isActive: true,
  };

  providerRates.value = originalProvider.value?.rates ?? [{
    id: 0,
    providerId: 0,
    serviceType: null,
    currency: CurrencyType.Usd,
    hourlyRate: null,
  }];
};

onMounted(loadProvider);
</script>

<style lang="scss" scoped>
#create-update-home-service-provider {
  .btn-block {
    width: auto;
    margin-bottom: 2px !important;
  }
}
</style>
